import type { RecipeSummary } from '@/api/types'
import { EffortBadge, RatingStars, VegetarianBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { CuisineDot } from '@/components/ui/CuisineAccent'
import { MacroStrip } from '@/components/ui/MacroStrip'
import { PreviewButton } from '@/components/ui/PreviewButton'
import { cn } from '@/lib/cn'
import { formatScore, relativeFromNow } from '@/lib/format'
import { summaryMacros } from '@/lib/macros'
import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ScoreBreakdown } from './ScoreBreakdown'
import type { ScoreRow } from './scoreRows'

/** One ranked suggestion: a meal-1 option or a partner for meal 1. */
export function CandidateCard({
  recipe,
  rank,
  score,
  rows,
  scoreLabel,
  selected,
  onPick,
  onPreview,
  footnote,
}: {
  recipe: RecipeSummary
  rank: number
  score: number
  rows: ScoreRow[]
  scoreLabel: string
  selected: boolean
  onPick: () => void
  onPreview: () => void
  footnote?: ReactNode
}) {
  const identity = [recipe.cuisine, recipe.protein_base].filter(Boolean).join(' · ')
  return (
    <Card className={cn('p-4', selected && 'border-accent ring-1 ring-accent')}>
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            'tabular grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold',
            rank === 1 ? 'bg-accent text-on-accent' : 'bg-surface-sunken text-ink-soft',
          )}
        >
          {rank}
        </span>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={`/recipes/${recipe.id}`}
                className="line-clamp-2 font-semibold text-ink hover:text-accent"
              >
                {recipe.name}
              </Link>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
                {identity ? (
                  <span className="inline-flex items-center gap-1.5">
                    <CuisineDot cuisine={recipe.cuisine} />
                    {identity}
                  </span>
                ) : null}
                <VegetarianBadge vegetarian={recipe.vegetarian} />
                <RatingStars value={recipe.rating} />
                <EffortBadge level={recipe.effort_level} />
                <span className="text-ink-faint">
                  {recipe.last_prep ? `Made ${relativeFromNow(recipe.last_prep)}` : 'Never made'}
                </span>
              </div>
            </div>
            <div className="flex shrink-0 items-center" title={scoreLabel}>
              <span className="tabular text-xl font-semibold text-ink">{formatScore(score)}</span>
              <ScoreBreakdown rows={rows} total={score} totalLabel={scoreLabel} />
            </div>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
            <div className="min-w-0 flex-1 basis-full space-y-1 sm:basis-0">
              <MacroStrip macros={summaryMacros(recipe)} />
              {footnote ? <p className="truncate text-xs text-ink-faint">{footnote}</p> : null}
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <PreviewButton onClick={onPreview} />
              <Button variant={selected ? 'primary' : 'secondary'} size="sm" onClick={onPick}>
                <Check size={14} aria-hidden />
                {selected ? 'Picked' : 'Pick'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
